import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { LoginManager } from '../../core/login-manager';
import { SaleManager } from '../../lib/sale-manager';
import { MESI, formatTime, refId } from '../../lib/format';
import { isAdminGlobale } from '../../lib/ruoli';
import {
    useEntityList, useEntityItem, statoLista, Field, DetailPage, DetailActions,
    ConfirmModal, messaggioErrore
} from './common';
import Select from '../../components/Select';
import TariffeSala from './tariffe';
import BlocchiSala from './blocchi';

const VUOTA = {
    nome: '', descrizione: '', capienza: '', orario_apertura: '08:00', orario_chiusura: '23:00',
    finestra_mesi: 2, anticipo_minimo_ore: 24, annullamento_ore: 48,
    max_prenotazioni_mese: 0, attiva: 1, idUserGroup: 0
};

/**
 * Fin dove si prenota oggi con una finestra di `mesi`: la regola la applica
 * il BookingEngine, qui la si traduce in un mese preciso, che si capisce
 * prima di un numero.
 */
function suggerimentoFinestra(mesi) {
    const n = parseInt(mesi, 10);
    if (Number.isNaN(n) || n < 0) return 'Mesi prenotabili dopo quello in corso, fino a fine mese.';
    const oggi = new Date();
    const ultimo = new Date(oggi.getFullYear(), oggi.getMonth() + n, 1);
    const mese = `${MESI[ultimo.getMonth()].toLowerCase()} ${ultimo.getFullYear()}`;
    return n === 0
        ? `Solo il mese in corso: oggi si prenota fino a fine ${mese}.`
        : `Mesi prenotabili dopo quello in corso: oggi si prenota fino a fine ${mese}.`;
}

/**
 * Pagina di una sala: i parametri che il BookingEngine applica alle richieste
 * e, sotto, le due cose che le appartengono — tariffe e blocchi.
 *
 * Finche' la sala non esiste non c'e' un id a cui agganciarle, quindi le
 * sottosezioni compaiono solo dopo il primo salvataggio.
 *
 * Il condominio di appartenenza lo sceglie solo l'amministratore globale, ed
 * e' l'unico campo che non viaggia con il resto del form: la CRUD del
 * framework non accetta `idUserGroup`, lo timbra da se' con il gruppo di chi
 * scrive. Ci pensa `assegna_sala` subito dopo il salvataggio.
 */
export default function Sala() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [userInfo] = useState(() => LoginManager.getUserInfo());
    const globale = isAdminGlobale(userInfo);

    const nuova = !id || id === 'nuova';
    const { item, loading, error, reload } = useEntityItem('sala', nuova ? null : id);
    const tariffe = useEntityList('tariffa', 'ordine', 'asc');
    const blocchi = useEntityList('blocco', 'data_inizio', 'desc');
    // solo l'amministratore globale sceglie: gli altri hanno un condominio solo
    const condomini = useEntityList(globale ? 'user_group' : null, 'name', 'asc');

    const [form, setForm] = useState(nuova ? { ...VUOTA } : null);
    const [saving, setSaving] = useState(false);
    const [daEliminare, setDaEliminare] = useState(false);
    const [working, setWorking] = useState(false);

    useEffect(() => {
        if (!item) return;
        setForm({
            nome: item.nome || '',
            descrizione: item.descrizione || '',
            capienza: item.capienza === null ? '' : item.capienza,
            orario_apertura: formatTime(item.orario_apertura),
            orario_chiusura: formatTime(item.orario_chiusura),
            finestra_mesi: item.finestra_mesi,
            anticipo_minimo_ore: item.anticipo_minimo_ore,
            annullamento_ore: item.annullamento_ore,
            max_prenotazioni_mese: item.max_prenotazioni_mese || 0,
            attiva: parseInt(item.attiva, 10) === 1 ? 1 : 0,
            idUserGroup: refId(item.idUserGroup)
        });
    }, [item]);

    function aggiorna(campo, valore) {
        setForm(prev => ({ ...prev, [campo]: valore }));
    }

    async function salva(e) {
        e.preventDefault();
        if (!form.nome.trim()) {
            toast.error('Il nome della sala è obbligatorio.', { theme: 'colored' });
            return;
        }
        if (form.orario_chiusura <= form.orario_apertura) {
            toast.error("L'orario di chiusura deve seguire quello di apertura.", { theme: 'colored' });
            return;
        }
        if (globale && !parseInt(form.idUserGroup, 10)) {
            toast.error('Scegli il condominio a cui appartiene la sala.', { theme: 'colored' });
            return;
        }

        const payload = {
            nome: form.nome.trim(),
            descrizione: form.descrizione,
            capienza: form.capienza === '' ? 0 : parseInt(form.capienza, 10),
            orario_apertura: `${form.orario_apertura}:00`,
            orario_chiusura: `${form.orario_chiusura}:00`,
            finestra_mesi: parseInt(form.finestra_mesi, 10) || 0,
            anticipo_minimo_ore: parseInt(form.anticipo_minimo_ore, 10) || 0,
            annullamento_ore: parseInt(form.annullamento_ore, 10) || 0,
            max_prenotazioni_mese: parseInt(form.max_prenotazioni_mese, 10) || 0,
            attiva: parseInt(form.attiva, 10)
        };

        setSaving(true);
        try {
            const res = nuova
                ? await DataManager.insertItem('sala', payload)
                : await DataManager.updateItem('sala', payload, item.id);

            if (!res || res.success !== 1) {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
                return;
            }

            // la create risponde con l'id in cima, non dentro `body`
            const idSala = nuova ? parseInt(res.id, 10) : item.id;

            /* Il condominio e' un secondo giro: la CRUD ha appena timbrato la
               sala con il gruppo di chi scrive, che per l'amministratore
               globale non e' mai quello giusto. */
            const scelto = parseInt(form.idUserGroup, 10) || 0;
            const attuale = nuova ? 0 : refId(item.idUserGroup);
            let assegnata = true;

            if (globale && idSala && scelto && scelto !== attuale) {
                const esito = await SaleManager.assegnaSala(idSala, scelto);
                assegnata = Boolean(esito && esito.success === 1);
                if (!assegnata) {
                    toast.error(
                        messaggioErrore(esito, 'Sala salvata, ma non assegnata al condominio.'),
                        { theme: 'colored' }
                    );
                }
                else if (!nuova) reload();
            }

            if (assegnata) {
                toast.success(nuova ? 'Sala creata.' : 'Sala aggiornata.', { theme: 'colored' });
            }
            // creata la sala si resta dentro: tariffe e blocchi vanno definiti
            if (nuova) navigate(idSala ? `/admin/sale/${idSala}` : '/admin/sale');
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function elimina() {
        setWorking(true);
        try {
            const res = await DataManager.deleteItem('sala', item.id);
            if (res && res.success === 1) {
                toast.success('Sala eliminata.', { theme: 'colored' });
                navigate('/admin/sale');
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    const stato = statoLista({ loading: loading, error: error, empty: !form, icon: 'door-open' });

    return (
        <DetailPage
            backTo="/admin/sale"
            backLabel="Torna alle sale"
            eyebrow="Spazi comuni"
            title={nuova ? 'Nuova sala' : (item ? item.nome : 'Sala')}
            subtitle="Questi parametri hanno la precedenza sui valori di default: sono quelli che il motore di prenotazione applica."
        >
            {stato || (
                <>
                    <form onSubmit={salva}>
                        <div className="lv-subsection">
                            <div className="lv-subsection-head">
                                <h3 className="lv-subsection-title">La sala</h3>
                            </div>
                            <div className="lv-form-grid">
                                <Field label="Nome" required>
                                    <input className="lv-input" value={form.nome} onChange={e => aggiorna('nome', e.target.value)} />
                                </Field>
                                <Field label="Capienza" hint="Numero massimo di persone.">
                                    <input
                                        type="number"
                                        min="0"
                                        className="lv-input"
                                        value={form.capienza}
                                        onChange={e => aggiorna('capienza', e.target.value)}
                                    />
                                </Field>
                                {globale && (
                                    <Field
                                        label="Condominio"
                                        required
                                        hint="Chi vede la sala e ci puo' prenotare. Si cambia finché la sala non ha prenotazioni attive."
                                    >
                                        <Select
                                            value={form.idUserGroup}
                                            onChange={v => aggiorna('idUserGroup', v)}
                                            options={[
                                                { value: '0', label: 'Scegli il condominio…' },
                                                ...condomini.items.map(g => ({ value: String(g.id), label: g.name }))
                                            ]}
                                        />
                                    </Field>
                                )}
                                <Field label="Stato" hint="Una sala non attiva non si può prenotare.">
                                    <Select
                                        value={form.attiva}
                                        onChange={v => aggiorna('attiva', v)}
                                        options={[
                                            { value: '1', label: 'Attiva' },
                                            { value: '0', label: 'Non attiva' }
                                        ]}
                                    />
                                </Field>
                                <Field label="Descrizione" wide>
                                    <textarea
                                        className="lv-input"
                                        rows={3}
                                        value={form.descrizione}
                                        onChange={e => aggiorna('descrizione', e.target.value)}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="lv-subsection">
                            <div className="lv-subsection-head">
                                <h3 className="lv-subsection-title">Orari di apertura</h3>
                            </div>
                            <div className="lv-form-grid">
                                <Field label="Apertura" required>
                                    <input
                                        type="time"
                                        className="lv-input"
                                        value={form.orario_apertura}
                                        onChange={e => aggiorna('orario_apertura', e.target.value)}
                                    />
                                </Field>
                                <Field label="Chiusura" required>
                                    <input
                                        type="time"
                                        className="lv-input"
                                        value={form.orario_chiusura}
                                        onChange={e => aggiorna('orario_chiusura', e.target.value)}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="lv-subsection">
                            <div className="lv-subsection-head">
                                <h3 className="lv-subsection-title">Regole di prenotazione</h3>
                            </div>
                            <div className="lv-form-grid">
                                <Field label="Anticipo minimo (ore)" hint="Preavviso richiesto per prenotare.">
                                    <input
                                        type="number"
                                        min="0"
                                        className="lv-input"
                                        value={form.anticipo_minimo_ore}
                                        onChange={e => aggiorna('anticipo_minimo_ore', e.target.value)}
                                    />
                                </Field>
                                <Field label="Annullamento (ore)" hint="Entro quante ore si annulla senza addebito.">
                                    <input
                                        type="number"
                                        min="0"
                                        className="lv-input"
                                        value={form.annullamento_ore}
                                        onChange={e => aggiorna('annullamento_ore', e.target.value)}
                                    />
                                </Field>
                                <Field label="Finestra di prenotazione (mesi)" hint={suggerimentoFinestra(form.finestra_mesi)}>
                                    <input
                                        type="number"
                                        min="0"
                                        className="lv-input"
                                        value={form.finestra_mesi}
                                        onChange={e => aggiorna('finestra_mesi', e.target.value)}
                                    />
                                </Field>
                                <Field label="Limite mensile per utente" hint="0 = nessun limite.">
                                    <input
                                        type="number"
                                        min="0"
                                        className="lv-input"
                                        value={form.max_prenotazioni_mese}
                                        onChange={e => aggiorna('max_prenotazioni_mese', e.target.value)}
                                    />
                                </Field>
                            </div>
                        </div>

                        <DetailActions saving={saving} submitLabel={nuova ? 'Crea sala' : 'Salva modifiche'}>
                            {!nuova && (
                                <button
                                    type="button"
                                    className="lv-btn lv-btn-ghost lv-btn-danger-ghost"
                                    onClick={() => setDaEliminare(true)}
                                >
                                    <FontAwesomeIcon icon={['fas', 'trash']} className="me-2" />
                                    Elimina
                                </button>
                            )}
                        </DetailActions>
                    </form>

                    {nuova ? (
                        <div className="lv-subsection">
                            <div className="lv-note">
                                Tariffe e blocchi si definiscono dopo aver creato la sala.
                            </div>
                        </div>
                    ) : (
                        <>
                            <TariffeSala
                                sala={item}
                                tariffe={tariffe.items.filter(t => refId(t.idSala) === item.id)}
                                onChanged={tariffe.reload}
                            />
                            <BlocchiSala
                                sala={item}
                                blocchi={blocchi.items.filter(b => refId(b.idSala) === item.id)}
                                onChanged={blocchi.reload}
                            />
                        </>
                    )}
                </>
            )}

            <ConfirmModal
                show={daEliminare}
                title="Eliminare la sala?"
                message={item
                    ? `«${item.nome}» sparirà dal calendario, con le sue tariffe e i suoi blocchi.`
                    : ''}
                confirmLabel="Elimina"
                danger
                working={working}
                onConfirm={elimina}
                onClose={() => setDaEliminare(false)}
            />
        </DetailPage>
    );
}
